import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PortalService } from '@api/portal.service';
import { UserMenuService } from '@api/user-menu.service';
import { SiteStateService } from '@states/site-state.service';
import { PortalStateService } from '@states/portal-state.service';
import { SiteVM } from '@models/backend-generated-models/models';
import { filter, switchMap, take } from 'rxjs/operators';
import * as moment from 'moment';

@Component({
  selector: 'app-modal-report-generator',
  templateUrl: './modal-report-generator.component.html',
  styleUrls: ['./modal-report-generator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalReportGeneratorComponent implements OnInit {

  reportViewerUrl: string = '';
  sitesList: SiteVM[] = [];
  selectedSite: SiteVM | null = null;

  scopes: string[] = ['Full Year Overview', 'Quarterly Report', 'Monthly Report', 'Custom Date Range'];
  selectedScope: string = 'Monthly Report';

  // Comparison Options
  comparisonModes: { label: string; value: string; scopes: string[] }[] = [
    { label: 'No comparison', value: 'none', scopes: ['Full Year Overview', 'Quarterly Report', 'Monthly Report', 'Custom Date Range'] },
    { label: 'Compare with previous year', value: 'previous_year', scopes: ['Full Year Overview'] },
    { label: 'Compare with same period last year', value: 'same_period_last_year', scopes: ['Quarterly Report', 'Monthly Report', 'Custom Date Range'] },
    { label: 'Compare with previous period', value: 'previous_period', scopes: ['Quarterly Report', 'Monthly Report', 'Custom Date Range'] }
  ];
  selectedComparisonMode: string = 'none';

  years: number[] = [];
  quarters: { label: string; value: string }[] = [
    { label: 'All Quarters', value: 'all' },
    { label: 'Q1 (Jan - Mar)', value: 'q1' },
    { label: 'Q2 (Apr - Jun)', value: 'q2' },
    { label: 'Q3 (Jul - Sep)', value: 'q3' },
    { label: 'Q4 (Oct - Dec)', value: 'q4' }
  ];
  months: { label: string; value: string }[] = [
    { label: 'All Months', value: 'all' },
    { label: 'January', value: '1' },
    { label: 'February', value: '2' },
    { label: 'March', value: '3' },
    { label: 'April', value: '4' },
    { label: 'May', value: '5' },
    { label: 'June', value: '6' },
    { label: 'July', value: '7' },
    { label: 'August', value: '8' },
    { label: 'September', value: '9' },
    { label: 'October', value: '10' },
    { label: 'November', value: '11' },
    { label: 'December', value: '12' }
  ];

  selectedYear: number = new Date().getFullYear();
  selectedQuarter: string = 'all';
  selectedMonth: string = 'all';

  startDate: Date = new Date();
  endDate: Date = new Date();

  constructor(
    private dialogRef: MatDialogRef<ModalReportGeneratorComponent>,
    private portalService: PortalService,
    private userMenuService: UserMenuService,
    private siteStateService: SiteStateService,
    private portalStateService: PortalStateService,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar
  ) {
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= currentYear - 10; y--) {
      this.years.push(y);
    }
  }

  ngOnInit(): void {
    this.userMenuService.getReportViewerURL$().pipe(take(1)).subscribe(url => {
      this.reportViewerUrl = url;
      this.cdr.markForCheck();
    });

    this.portalStateService.selectedPortal$.pipe(
      filter(p => !!p),
      switchMap(p => this.portalService.getSitesForPortal$(p!.portalId))
    ).subscribe(sites => {
      this.sitesList = sites || [];
      const currentSite = this.siteStateService.selectedSiteSource.getValue();
      if (currentSite) {
        this.selectedSite = this.sitesList.find(s => s.siteId === currentSite.siteId) || currentSite;
      } else if (this.sitesList.length > 0) {
        this.selectedSite = this.sitesList[0];
      }
      this.cdr.markForCheck();
    });
  }

  onSiteChange(): void {
  }

  onYearChange(): void {
  }

  onScopeChange(): void {
    const validModes = this.comparisonModes
      .filter(m => m.scopes.includes(this.selectedScope))
      .map(m => m.value);
    
    if (!validModes.includes(this.selectedComparisonMode)) {
      this.selectedComparisonMode = 'none';
    }
  }

  get isPreviewValid(): boolean {
    if (!this.selectedSite) return false;

    if (this.selectedScope === 'Custom Date Range') {
      if (!this.startDate || !this.endDate) return false;
      if (new Date(this.startDate) > new Date(this.endDate)) return false;
    }

    return true;
  }

  get isFormValid(): boolean {
    return this.isPreviewValid;
  }

  calculateDates(): { start: Date; end: Date; compStart: Date | null; compEnd: Date | null } {
    const start = new Date();
    const end = new Date();
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    let compStart: Date | null = new Date();
    let compEnd: Date | null = new Date();
    compStart.setHours(0, 0, 0, 0);
    compEnd.setHours(23, 59, 59, 999);

    if (this.selectedScope === 'Full Year Overview') {
      start.setFullYear(this.selectedYear, 0, 1);
      end.setFullYear(this.selectedYear, 11, 31);
    } else if (this.selectedScope === 'Quarterly Report') {
      if (this.selectedQuarter === 'all') {
        start.setFullYear(this.selectedYear, 0, 1);
        end.setFullYear(this.selectedYear, 11, 31);
      } else {
        const qIndex = parseInt(this.selectedQuarter.replace('q', ''));
        const qStartMonth = (qIndex - 1) * 3;
        start.setFullYear(this.selectedYear, qStartMonth, 1);
        end.setFullYear(this.selectedYear, qStartMonth + 3, 0);
      }
    } else if (this.selectedScope === 'Monthly Report') {
      if (this.selectedMonth === 'all') {
        start.setFullYear(this.selectedYear, 0, 1);
        end.setFullYear(this.selectedYear, 11, 31);
      } else {
        const mIndex = parseInt(this.selectedMonth);
        start.setFullYear(this.selectedYear, mIndex - 1, 1);
        end.setFullYear(this.selectedYear, mIndex, 0);
      }
    } else if (this.selectedScope === 'Custom Date Range') {
      start.setTime(this.startDate ? new Date(this.startDate).getTime() : new Date().getTime());
      end.setTime(this.endDate ? new Date(this.endDate).getTime() : new Date().getTime());
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    }

    if (compStart && compEnd) {
      compStart.setTime(start.getTime());
      compEnd.setTime(end.getTime());
    }

    if (this.selectedComparisonMode === 'previous_year' || this.selectedComparisonMode === 'same_period_last_year') {
      if (compStart && compEnd) {
        compStart.setFullYear(start.getFullYear() - 1);
        compEnd.setFullYear(end.getFullYear() - 1);
      }
    } else if (this.selectedComparisonMode === 'previous_period') {
      if (this.selectedScope === 'Full Year Overview') {
        if (compStart && compEnd) {
          compStart.setFullYear(start.getFullYear() - 1);
          compEnd.setFullYear(end.getFullYear() - 1);
        }
      } else if (this.selectedScope === 'Quarterly Report') {
        if (this.selectedQuarter === 'all') {
          if (compStart && compEnd) {
            compStart.setFullYear(start.getFullYear() - 1);
            compEnd.setFullYear(end.getFullYear() - 1);
          }
        } else {
          if (compStart && compEnd) {
            compStart.setMonth(start.getMonth() - 3);
            compEnd.setMonth(end.getMonth() - 3);
          }
        }
      } else if (this.selectedScope === 'Monthly Report') {
        if (this.selectedMonth === 'all') {
          if (compStart && compEnd) {
            compStart.setFullYear(start.getFullYear() - 1);
            compEnd.setFullYear(end.getFullYear() - 1);
          }
        } else {
          if (compStart && compEnd) {
            compStart.setMonth(start.getMonth() - 1);
            compEnd.setMonth(end.getMonth());
            compEnd.setDate(0);
          }
        }
      } else if (this.selectedScope === 'Custom Date Range') {
        if (compStart && compEnd) {
          const durationMs = end.getTime() - start.getTime();
          compStart.setTime(start.getTime() - durationMs - 1000);
          compEnd.setTime(end.getTime() - durationMs - 1000);
          compStart.setHours(0, 0, 0, 0);
          compEnd.setHours(23, 59, 59, 999);
        }
      }
    }

    return { start, end, compStart, compEnd };
  }

  generateReport(): void {
    if (!this.selectedSite || !this.selectedSite.siteId) return;

    if (!this.reportViewerUrl) {
      this.snackBar.open('Unable to generate the energy report. Please check that the ReportViewer service is available.', 'OK', {
        duration: 5000,
        panelClass: ['warn-snackbar']
      });
      return;
    }

    const { start, end, compStart, compEnd } = this.calculateDates();
    const dateLeftStr = moment(start).format('YYYY-MM-DD HH:mm:ss');
    const dateRightStr = moment(end).format('YYYY-MM-DD HH:mm:ss');
    const compLeftStr = compStart ? moment(compStart).format('YYYY-MM-DD HH:mm:ss') : '';
    const compRightStr = compEnd ? moment(compEnd).format('YYYY-MM-DD HH:mm:ss') : '';

    let scopeCode = 'monthly';
    if (this.selectedScope === 'Full Year Overview') scopeCode = 'year_overview';
    else if (this.selectedScope === 'Quarterly Report') scopeCode = 'quarterly';
    else if (this.selectedScope === 'Custom Date Range') scopeCode = 'custom';

    let resolvedPeriod = '1d';
    if (this.selectedScope === 'Full Year Overview') {
      resolvedPeriod = '1m';
    } else if (this.selectedScope === 'Quarterly Report') {
      resolvedPeriod = '1m';
    } else if (this.selectedScope === 'Monthly Report') {
      resolvedPeriod = this.selectedMonth === 'all' ? '1m' : '1d';
    } else if (this.selectedScope === 'Custom Date Range') {
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      resolvedPeriod = diffDays <= 45 ? '1d' : '1m';
    }

    const params: Record<string, string> = {
      aPortalID: String(this.portalStateService.selectedPortalSource.getValue()?.portalId || ''),
      aSiteID: String(this.selectedSite.siteId),
      aReportScope: scopeCode,
      aPeriod: resolvedPeriod,
      aYear: scopeCode === 'custom' ? 'all' : String(this.selectedYear),
      aQuarter: scopeCode === 'quarterly' ? String(this.selectedQuarter) : 'all',
      aMonth: scopeCode === 'monthly' ? String(this.selectedMonth) : 'all',
      aDateLeft: dateLeftStr,
      aDateRight: dateRightStr,
      aComparisonMode: this.selectedComparisonMode,
      aShowExecSummary: 'true'
    };

    if (this.selectedComparisonMode === 'none') {
      params['aComparisonDateLeft:isnull'] = 'true';
      params['aComparisonDateRight:isnull'] = 'true';
    } else {
      params['aComparisonDateLeft'] = compLeftStr;
      params['aComparisonDateRight'] = compRightStr;
    }

    const siteId = this.selectedSite.siteId!;
    const portalId = this.portalStateService.selectedPortalSource.getValue()?.portalId || 0;

    const base0 = this.reportViewerUrl.endsWith('/') ? this.reportViewerUrl : (this.reportViewerUrl + '/');
    const base = base0.replace(/\/DCS\.\/*$/i, "/DCS.Reports/");
    const renderBits = '&rs:Command=Render&rs:Format=PDF';
    const enc = (v: string) => encodeURIComponent(v).replace(/%3A/gi, ':');

    const newWindow = window.open('about:blank', '_blank');
    const reportPath = '/DCS.Reports/rptConsolidatedEnergyReport';
    this.userMenuService.getReportTicket$(siteId, portalId, reportPath).subscribe({
      next: (ticket) => {
        params['aTicket'] = ticket;
        const reportName = 'rptConsolidatedEnergyReport';
        const qs = Object.keys(params)
          .map(k => `${enc(k)}=${enc(params[k])}`)
          .join('&');
        const fullUrl = `${base}${reportName}${renderBits}&${qs}`;
        if (newWindow) {
          newWindow.location.href = fullUrl;
        } else {
          window.open(fullUrl, '_blank');
        }
      },
      error: (err) => {
        console.error('Failed to generate security ticket:', err);
        if (newWindow) {
          newWindow.close();
        }
        this.snackBar.open('Unable to generate the energy report. Please check that the ReportViewer service is available.', 'OK', {
          duration: 5000,
          panelClass: ['warn-snackbar']
        });
      }
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
